/**
 * NPC pirate raids (GDD §3 "kleine NPC-Piraten", §2 "Siege in NPC-Skirmishes"
 * as an XP source).
 *
 * This is the loop that gives Abwehr a reason to exist before PvP lands:
 * a raid fleet arrives on a timer, the battle resolves against the colony's
 * defences and stationed ships, and the outcome is real either way — a win
 * grants XP and leaves harvestable debris in orbit, a loss lets the pirates
 * plunder up to the GDD's 50 % cap.
 *
 * Raid strength scales with what the player actually has, so the fight stays
 * meaningful without ever being unwinnable: the budget is capped below the
 * defender's own power (see RAID_POWER_RATIO).
 */

import type { GameState, UnitKey } from './types.js';
import { DEFENSE_KEYS, SHIP_KEYS, UNITS } from './data/units.js';
import { nextInt, nextRandom } from './rng.js';
import { PLUNDER_CAP } from './formulas.js';
import { buildingLevel } from './state.js';

/**
 * A raid is budgeted at this fraction of the defender's combat power. Below 1
 * on purpose: pirates are a pressure mechanic, not a wipe. A player who never
 * builds defences still faces the floor budget and will lose resources.
 */
export const RAID_POWER_RATIO = 0.55;

/** Minimum raid budget so a bare colony still gets raided (design fill-in). */
const RAID_FLOOR_POWER = 600;

/** Ships pirates field, cheapest first — the budget is spent down this list. */
const PIRATE_ROSTER: UnitKey[] = ['corvette', 'cruiser', 'battleship'];

/** Combat power of a unit: what it can deal and what it can take. */
function unitPower(key: UnitKey): number {
  const def = UNITS[key];
  return def.damage * 2 + def.hull + def.shield;
}

/** Total combat power of the colony (defences + stationed ships). */
export function defenderPower(state: GameState): number {
  let power = 0;
  for (const key of DEFENSE_KEYS) power += state.planets[0]!.defense[key] * unitPower(key);
  for (const key of SHIP_KEYS) power += state.planets[0]!.ships[key] * unitPower(key);
  return power;
}

/** The defending party for `simulateBattle`. */
export function defenderParty(state: GameState) {
  const units: Partial<Record<UnitKey, number>> = {};
  for (const key of DEFENSE_KEYS) {
    if (state.planets[0]!.defense[key] > 0) units[key] = state.planets[0]!.defense[key];
  }
  for (const key of SHIP_KEYS) {
    if (state.planets[0]!.ships[key] > 0) units[key] = state.planets[0]!.ships[key];
  }
  return { units, shieldTech: state.research.shieldHarmonics };
}

/**
 * Roll a pirate fleet for the given budget. Mutates `state.rngSeed`.
 * Spends the budget from the heaviest affordable hull downwards so a strong
 * colony faces real ships instead of a swarm of corvettes.
 */
export function rollPirateFleet(state: GameState, budget: number): Partial<Record<UnitKey, number>> {
  const fleet: Partial<Record<UnitKey, number>> = {};
  let remaining = Math.max(RAID_FLOOR_POWER, budget);

  for (const key of [...PIRATE_ROSTER].reverse()) {
    const power = unitPower(key);
    if (power > remaining) continue;
    // Spend 40–70 % of what is left on this class, rest trickles down.
    const shareRoll = nextRandom(state.rngSeed);
    state.rngSeed = shareRoll.seed;
    const share = key === 'corvette' ? 1 : 0.4 + shareRoll.value * 0.3;
    const count = Math.floor((remaining * share) / power);
    if (count > 0) {
      fleet[key] = count;
      remaining -= count * power;
    }
  }

  // Always field at least one hull, however poor the colony.
  if (Object.keys(fleet).length === 0) fleet.corvette = 1;
  return fleet;
}

/** Warning lead time before the raid fleet arrives (Phalanx shortens it). */
export function raidLeadTimeMs(state: GameState): number {
  // Base 45 min; the Subraum-Phalanx buys earlier warning (GDD §3).
  const phalanxBonus = Math.min(30, buildingLevel(state, 'phalanx') * 5);
  return (45 + phalanxBonus) * 60_000;
}

/** Plunder the pirates take on a successful raid — capped at 50 % (GDD §5). */
export function plunderAmounts(
  state: GameState,
  seed: number,
): { taken: { titan: number; silicate: number; isotope: number }; seed: number } {
  const roll = nextInt(seed, 25, Math.round(PLUNDER_CAP * 100));
  const fraction = roll.value / 100;
  return {
    taken: {
      titan: Math.floor(state.resources.titan * fraction),
      silicate: Math.floor(state.resources.silicate * fraction),
      isotope: Math.floor(state.resources.isotope * fraction),
    },
    seed: roll.seed,
  };
}
